<template>
  <form action="false" @submit.prevent="handleRun">
    <div id="autorun">
      <div class="modal_inputs">
        <Multiselect
          v-model="selectedTemplate"
          :options="templateList"
          :searchable="true"
          placeholder="Select a Template"
          type="select"
        />
        <p>OR</p>
        <div class="flex gap-2 flex-input">
          <FormKit v-model="rows" min="1" placeholder="Rows" type="number" value="" />
          <FormKit v-model="cols" min="1" placeholder="Columns" type="number" value="" />
          <Button secondary-button text="New Template" @click="generateTemplate" />
        </div>

        <div v-if="templateData" class="modal_inputs">
          <div class="flex gap-2">
            <FormKit
              v-model="templateData.name"
              input-class="h-10 w-60 text-ellipsis"
              outer-class="m-0"
              placeholder="Template Name"
            />
          </div>
          <table class="max-w-full w-full">
            <tbody>
              <tr v-for="row in templateData.rows" :key="`row${row}`">
                <td v-for="col in templateData.cols" :key="`col${col}`">
                  <Multiselect
                    v-model="templateData.templates[spotIndex(row, col)]"
                    :options="inventoriesOptions"
                    :searchable="true"
                    placeholder="Inventory"
                    type="select"
                  />
                </td>
              </tr>
            </tbody>
          </table>
          <div class="flex gap-2 justify-between">
            <FormKit
              v-if="showSave"
              v-model="templateData.save"
              :classes="{
                fieldset: 'border-0! p-0! mt-2',
              }"
              :label="showSaveText"
              type="checkbox"
              value="1"
            />
            <FormKit
              v-if="templateData.dirty"
              v-model="templateData.save_as"
              label="Save As New Template"
              type="checkbox"
              value="1"
            />
          </div>
        </div>
      </div>
      <div class="modal_actions">
        <Button secondary-button text="Cancel" @click="handleCancel"></Button>
        <Button text="Start Auto Run" @click="handleRun" />
      </div>
    </div>
  </form>
</template>

<script setup>
import { ref, onMounted, computed, watch, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { useModal } from "@/components/UI/Modal/useModal";
import Multiselect from "@/components/UI/Form/Multiselect/Multiselect.vue";
import Button from "@/components/UI/Form/Button/Button.vue";
import { useProductionSearch } from "@/components/Production/ProductionTableSearch/ProductionTableSearch.hook";

import { autoRunStatusApi, autoRunStartApi, autoRunTemplatesApi } from "@/api/production";
import { useAppStoreActions } from "@/hooks/store/useApp";
import useToast from "@/hooks/useToast";
import { useUserStoreActions } from "@/hooks/store/useUserStore";
import ROLES from "@/consts/userRoles";

// This modal only picks a template and starts a run. The burn table lives on
// /crm/laser-table/:id, where the Redo and Approve dialogs can open over it
// without unmounting it — the modal has a single slot, so a workshop rendered
// here is destroyed by its own child dialogs.
const props = defineProps({
  onClose: Function,
});
const { close } = useModal();
const router = useRouter();

// A status request still in flight after the operator cancels must not drag
// them off the Production page seconds later.
const active = ref(true);
const cols = ref("");
const rows = ref("");
const templates = ref([]);
const selectedTemplate = ref(null);
const templateData = ref(null);
const { inventoriesOptions } = useProductionSearch();
const { setLoading } = useAppStoreActions();
const { openToast } = useToast();
const { profile } = useUserStoreActions();

const templateList = computed(() =>
  templates.value.map((template) => ({ label: template.name, value: template }))
);
const isLaserPrinter = computed(() => profile.value?.group_id === ROLES.LASER_PRINTERS.id);
const showSave = computed(
  () => (templateData.value && templateData.value.new) || !!templateData.value.dirty
);
const showSaveText = computed(() =>
  templateData.value && templateData.value.new ? "Save Template" : "Update Template"
);

// Spot 0 is the bottom-left corner of the physical table: sortAndFill() paints
// the burn sequence from there (Laser.helpers.js). The grid has to be numbered
// from the same corner, or what the operator draws on the top row is burned on
// the machine's bottom row.
const spotIndex = (row, col) =>
  (templateData.value.rows - row) * templateData.value.cols + (col - 1);

const goToLaserTable = (laserOrderId) => {
  router.push(`/crm/laser-table/${laserOrderId}`);
  close();
};

// Resolves the run that is currently active for this user's printer and hands
// the operator over to its laser table. Used both on open (a run is already
// going) and right after starting one.
const goToRunningLaserTable = () => {
  if (!isLaserPrinter.value) {
    return Promise.resolve(false);
  }
  return autoRunStatusApi()
    .then((response) => {
      if (!active.value) {
        return false;
      }
      const laserOrderId = response.data?.laser_order_id;
      if (laserOrderId) {
        goToLaserTable(laserOrderId);
        return true;
      }
      return false;
    })
    .catch((error) => {
      openToast(error.response?.data?.message, { type: "error" });
      return false;
    });
};

const handleAutoRun = (data) => {
  setLoading(true);
  autoRunStartApi(data)
    .then(() => {
      openToast("Auto Run started");
      return goToRunningLaserTable();
    })
    .catch((error) => {
      openToast(error.response.data.message, { type: "error" });
    })
    .finally(() => {
      setLoading(false);
    });
};

const handleCancel = () => {
  close();
};

const handleRun = () => {
  if (templateData.value) {
    handleAutoRun(templateData.value);
  }
};

const generateTemplate = () => {
  templateData.value = {
    rows: parseInt(rows.value, 10),
    cols: parseInt(cols.value, 10),
    templates: Array.from({ length: rows.value * cols.value }, () => null),
    new: true,
    save: false,
    dirty: false,
  };
};

onMounted(() => {
  setLoading(true);
  goToRunningLaserTable();
  autoRunTemplatesApi()
    .then((res) => {
      templates.value = res.data;
    })
    .finally(() => {
      setLoading(false);
    });
});
onUnmounted(() => {
  active.value = false;
  props.onClose();
});
watch(selectedTemplate, (template) => {
  if (template) {
    templateData.value = JSON.parse(JSON.stringify(template));
    templateData.value.new = false;
    templateData.value.save = false;
    templateData.value.save_as = false;
  }
});
// Watch for changes of name element in the templateData object
watch(
  templateData,
  (template) => {
    if (selectedTemplate.value) {
      if (
        template.name !== selectedTemplate.value.name ||
        JSON.stringify(template.templates) !== JSON.stringify(selectedTemplate.value.templates)
      ) {
        templateData.value.dirty = true;
      } else {
        templateData.value.dirty = false;
      }
    }
  },
  { deep: true }
);
</script>

<style>
.modal_popup:has(#autorun) {
  max-width: 800px;
}

.flex-input > div {
  flex-basis: 100px;
}

#autorun table td {
  border: 1px solid #e8ecee;
}
</style>
